import { useEffect, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { BookmarkSimple, Trash, CaretDown, CaretRight, Flask, Cube, FilePdf, CheckSquare, Square, Files } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { exportReportPDF } from "@/lib/pdf";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Saved() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const batchRef = useRef(null);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((i) => i.id)));
  const clearSel = () => setSelected(new Set());

  const exportBatch = () => {
    if (selected.size === 0 || !batchRef.current) return;
    exportReportPDF({
      title: "Saved Reports Pack",
      subtitle: `${selected.size} report${selected.size !== 1 ? "s" : ""} · Thermal Spray Atlas`,
      bodyHTML: batchRef.current.innerHTML,
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/suggestions");
      setItems(res.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    else if (!authLoading) setLoading(false);
  }, [user, authLoading]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/suggestions/${deleteTarget.id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportPdf = (it) => {
    const node = document.getElementById(`report-${it.id}`);
    exportReportPDF({
      title: it.title || it.query,
      subtitle: it.mode === "material" ? "Material → Parameters Report" : "Properties → Material Report",
      bodyHTML: node ? node.innerHTML : it.content,
    });
  };

  if (authLoading || loading) {
    return <div className="grain min-h-[50vh] flex items-center justify-center font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>;
  }

  if (!user) {
    return (
      <div className="grain mx-auto max-w-2xl px-4 py-20 text-center">
        <BookmarkSimple size={40} className="text-muted-foreground mx-auto mb-3" />
        <h1 className="font-heading font-black text-white text-2xl">Sign in to view saved suggestions</h1>
        <p className="text-sm text-muted-foreground mt-2">Head to the Admin tab to sign in, then your saved advisor reports will appear here.</p>
      </div>
    );
  }

  return (
    <div className="grain mx-auto max-w-[1000px] px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <BookmarkSimple size={26} weight="bold" className="text-primary" />
          <div>
            <h1 className="font-heading font-black text-white text-2xl">Saved Suggestions</h1>
            <p className="text-sm text-muted-foreground">{items.length} saved report{items.length !== 1 ? "s" : ""}.</p>
          </div>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              data-testid="select-all-btn"
              onClick={() => (selected.size === items.length ? clearSel() : selectAll())}
              className="text-xs font-mono uppercase tracking-wide text-muted-foreground hover:text-white px-2 py-1"
            >
              {selected.size === items.length ? "Clear" : "Select all"}
            </button>
            <Button
              data-testid="batch-pdf-btn"
              onClick={exportBatch}
              disabled={selected.size === 0}
              className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest disabled:opacity-40"
            >
              <Files size={16} weight="bold" className="mr-1.5" /> Export Selected ({selected.size})
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center">
          <p className="font-heading text-white text-lg">Nothing saved yet</p>
          <p className="text-sm text-muted-foreground mt-1">Generate a suggestion on the Advisor and hit Save to keep it here.</p>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {items.map((it) => {
            const open = openId === it.id;
            return (
              <div key={it.id} data-testid={`saved-${it.id}`} className="bg-card">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    data-testid={`saved-select-${it.id}`}
                    onClick={() => toggleSelect(it.id)}
                    className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                    aria-label="Select report"
                  >
                    {selected.has(it.id) ? <CheckSquare size={18} weight="fill" className="text-primary" /> : <Square size={18} />}
                  </button>
                  <button
                    data-testid={`saved-toggle-${it.id}`}
                    onClick={() => setOpenId(open ? null : it.id)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
                    <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm ${it.mode === "material" ? "text-primary border-primary/40 bg-primary/10" : "text-secondary border-secondary/40 bg-secondary/10"}`}>
                      {it.mode === "material" ? <Flask size={11} className="inline mr-1" /> : <Cube size={11} className="inline mr-1" />}
                      {it.mode}
                    </span>
                    <span className="text-white text-sm truncate">{it.title || it.query}</span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground shrink-0 hidden sm:block">
                      {new Date(it.created_at).toLocaleDateString()} · {it.process}
                    </span>
                  </button>
                  <Button
                    data-testid={`saved-pdf-${it.id}`}
                    onClick={() => { if (!open) setOpenId(it.id); setTimeout(() => exportPdf(it), 50); }}
                    variant="ghost" size="icon"
                    className="h-8 w-8 rounded-sm text-muted-foreground hover:text-primary shrink-0"
                  >
                    <FilePdf size={16} />
                  </Button>
                  <Button
                    data-testid={`saved-delete-${it.id}`}
                    onClick={() => setDeleteTarget(it)}
                    variant="ghost" size="icon"
                    className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive shrink-0"
                  >
                    <Trash size={16} />
                  </Button>
                </div>
                {open && (
                  <div id={`report-${it.id}`} className="report text-sm px-6 pb-6 pt-1 border-t border-border">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden container used to build the combined batch PDF */}
      <div ref={batchRef} aria-hidden style={{ position: "absolute", left: "-99999px", top: 0, width: "800px" }}>
        {items.filter((i) => selected.has(i.id)).map((it) => (
          <div key={it.id} className="report" style={{ pageBreakAfter: "always" }}>
            <h1>{it.title || it.query}</h1>
            <p><strong>Mode:</strong> {it.mode} &nbsp; <strong>Process:</strong> {it.process}</p>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.content}</ReactMarkdown>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-white">Delete saved suggestion?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="saved-confirm-delete" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
